import { useCallback, useEffect, useMemo, useSyncExternalStore } from "react";
import {
  attachmentContentSchema,
  attachmentResponseSchema,
  uuidv7,
  type AttachableType,
  type Attachment,
  type AttachmentContent,
  type ContentBlob,
  type CreateAttachmentFields,
} from "@vmeste/shared";
import { t, useLocale } from "../i18n/index.js";
import { selectAttachments } from "../lib/store.js";
import { syncEngine } from "../lib/sync.js";
import {
  dropQueuedUpload,
  queueUpload,
  queuedUpload,
  queuedUploads,
  sealForUpload,
  withoutMetadata,
  type QueuedUpload,
} from "../lib/media.js";
import { plaintextOf } from "../lib/plaintext.js";

/**
 * Attachments (SPEC §4.5, §4.6).
 *
 * A file hangs off a wish, a message, a list item or an event. Reading is like
 * everything else on this side of the app — the local mirror answers, and a
 * delta or a WebSocket nudge keeps it current, so the other person's photo
 * appears without this screen asking for it.
 *
 * Writing is the part that is deliberately different: **an upload does not go
 * through the outbox.** That queue lives in localStorage and holds small JSON
 * operations; a 10 MB photo has no business in it, and putting one there would
 * blow the storage quota and take the queue — and with it every unsent list
 * change — down with it. So the bytes go straight out over their own request.
 *
 * They do have a queue of their own now, in IndexedDB, which holds a `Blob` as
 * a `Blob` and so has none of the problems that ruled the outbox out (see
 * `lib/media`). A file picked with no network is kept there and sent when there
 * is one; a file whose request dies mid-flight goes back. What has *not*
 * changed is the order: an attachment hangs off a row the server has already
 * seen, so this queue is drained only once the outbox is empty, and the chat
 * composer still waits for its message to land before handing the bytes over.
 *
 * `name` and `caption` live inside the content blob like every other thing a
 * person typed (SPEC §3); nothing here reads them off the server's metadata.
 */

/*
 * The file's name and caption are written with the space's own codec (SPEC §3),
 * asked for at the moment of upload. `null` means the space is encrypted and
 * this device has no key, and then nothing goes up at all — the alternative is
 * a filename anybody with the bucket can read, sitting beside content that was
 * encrypted precisely so they could not.
 */

/** Mirrors the server's allow-list exactly, so the picker cannot offer a refusal. */
export const ALLOWED_MIME = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/avif",
  "application/pdf",
] as const;

/**
 * What the picker offers — deliberately wider than what is accepted.
 *
 * It used to be the allow-list itself, on the reasoning that a picker should
 * not offer a refusal. On a phone that reasoning inverted: an iPhone's photos
 * are HEIC, which is not on the list, so naming the types exactly left the
 * camera roll greyed out and the person unable to send a photograph at all.
 *
 * `image/*` is the ask iOS answers by handing over a JPEG instead — the
 * conversion happens in the system picker, before the file reaches us. The
 * allow-list below still decides what is actually sent, so a type that slips
 * through is refused with a sentence rather than by a picker that will not
 * open.
 *
 * HEIC is not added to the allow-list on purpose: Safari renders it and the
 * other browsers do not, so accepting one would mean a photograph the person
 * on Android simply cannot see.
 */
export const ACCEPT_ATTACHMENTS = "image/*,application/pdf";

/**
 * The picture-only subset, for a picker that has no business offering a PDF —
 * an avatar. Derived from the list above rather than written out again, exactly
 * as the server derives its own image subset from the one allow-list it has.
 */
export const ACCEPT_IMAGES = "image/*";

/** The server's ceiling. Checked here too, so a doomed 10 MB upload never starts. */
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

export interface AttachmentTarget {
  type: AttachableType;
  id: string;
}

/** An attachment as the strip renders it: metadata plus the decoded blob. */
export interface AttachmentView {
  id: string;
  /** A path on our own origin (`/api/media/…`). Never anywhere else. */
  url: string;
  mime: string;
  bytes: number;
  name: string;
  caption?: string;
  /**
   * What the server says this is — which in an encrypted space is nothing at
   * all, because the type travels inside the envelope. A guess to render with
   * until the file is opened, never the last word: see `useMedia`.
   */
  isImage: boolean;
  addedBy: string;
  createdAt: string;
}

/** A file on its way up, or one that did not make it. */
export interface PendingUpload {
  id: string;
  name: string;
  bytes: number;
  /** 0…1. Real bytes on the wire, not a spinner pretending. */
  progress: number;
  /**
   * Waiting for a network rather than climbing towards one.
   *
   * Without it a queued file draws as an upload stuck at nought, which is what
   * a failed one looks like — and this is the opposite of a failure: the bytes
   * are safe on disk and will go by themselves.
   */
  queued: boolean;
  error: string | null;
}

/** What a caller may say about a file beyond the file itself. */
export interface UploadOptions {
  /** The name to store, when it is not the one on disk. */
  name?: string;
  /**
   * The id this upload's row — and the attachment it becomes — will have.
   *
   * For a caller that has the file on screen before the upload starts: the
   * chat composer draws a preview at the moment of picking and needs to know
   * afterwards which row carries that picture's percentage. Must be a uuid v7,
   * because it is the id the server is asked to create the attachment under.
   */
  id?: string;
}

export interface AttachmentActions {
  attachments: AttachmentView[];
  /**
   * Uploads in flight, or stopped.
   *
   * With a target: that target's. **Without one: every upload in the space** —
   * which is the only way a screen can watch a file whose target it learns
   * about after the fact, the way the chat composer learns a message's id only
   * once the message has been sent.
   */
  pending: PendingUpload[];
  /** True until this space has been synced once — the strip reserves space meanwhile. */
  loading: boolean;
  /** A failed delete. Upload failures live on their own pending row. */
  error: string | null;
  /**
   * Resolves to the reason it did not go, or `null` when the file is dealt
   * with — up, or waiting in the queue for a network.
   *
   * Those two are one answer on purpose. `null` is what tells a caller holding
   * its own preview to let go of the file, and a queued upload is exactly that:
   * the bytes are safe on disk and the row on `pending` is what shows them from
   * here on, so a second copy in a composer would be two tiles for one file.
   */
  upload(target: AttachmentTarget, file: File, options?: UploadOptions): Promise<string | null>;
  /**
   * Write a file to disk now, to be sent once its target exists.
   *
   * For the one caller whose target does not exist yet: a chat message leaves
   * through the outbox, so the row a photograph hangs off may be minutes away —
   * or a reload away. Held in memory it was simply lost, silently, and the
   * message arrived without the picture that was the point of it.
   *
   * Returns `null` when the file is safely on disk, or the sentence saying why
   * it is not.
   */
  keep(target: AttachmentTarget, file: File, options?: UploadOptions): Promise<string | null>;
  /** Send a file `keep` has already put on disk. Same answer as `upload`. */
  sendKept(pendingId: string): Promise<string | null>;
  /** Drop a kept file from the disk and the screen — its target never arrived. */
  forget(pendingId: string): Promise<void>;
  remove(attachmentId: string): Promise<void>;
  /** Drop a failed upload from the strip. */
  dismiss(pendingId: string): void;
}

/* --------------------------------------------------------------- messages */

/**
 * Asked for at the moment of failure, never held in a constant: a message read
 * once at module load would keep the language the app started in, and the
 * language can change while the app is running.
 */
const offlineMessage = (): string => t("attachments.errorOffline");
const unsupportedMessage = (): string => t("attachments.errorUnsupported");
const tooLargeMessage = (): string =>
  t("attachments.errorTooLarge", { size: Math.round(MAX_ATTACHMENT_BYTES / 1024 / 1024) });

/* ---------------------------------------------------- in-flight and local */

/**
 * Module-level rather than component state, for the same reason the unfurl
 * tracker on the wishes screen is: a strip is unmounted the moment somebody
 * switches tabs, and an upload in flight has to survive that and still land.
 */
type Key = string;

const keyOf = (spaceId: string, target: AttachmentTarget | null): Key =>
  target ? `${spaceId}|${target.type}|${target.id}` : `${spaceId}|*|*`;

const uploads = new Map<Key, PendingUpload[]>();
const removeErrors = new Map<Key, string>();

/**
 * Ids deleted here but not yet gone from the mirror.
 *
 * The store is the server's word, and this device cannot write to it; without
 * this the thumbnail would sit there until the pull came back, which on a slow
 * connection reads as a button that did nothing. The entry is dropped as soon as
 * the mirror agrees.
 */
const removed = new Set<string>();

const listeners = new Set<() => void>();
let version = 0;

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const snapshot = (): number => version;

function emit(): void {
  version += 1;
  for (const listener of listeners) listener();
}

function pendingOf(key: Key): PendingUpload[] {
  return uploads.get(key) ?? [];
}

/**
 * Every upload in one space, whatever it is going to hang off.
 *
 * A space id is a uuid and a key joins on `|`, so the prefix cannot match a
 * neighbouring space.
 */
function pendingIn(spaceId: string): PendingUpload[] {
  const prefix = `${spaceId}|`;
  const rows: PendingUpload[] = [];
  for (const [key, list] of uploads) {
    if (key.startsWith(prefix)) rows.push(...list);
  }
  return rows;
}

/** The key one row lives under, for a caller that only knows the row's id. */
function keyHolding(spaceId: string, pendingId: string): Key | null {
  const prefix = `${spaceId}|`;
  for (const [key, list] of uploads) {
    if (key.startsWith(prefix) && list.some((row) => row.id === pendingId)) return key;
  }
  return null;
}

function setPending(key: Key, rows: PendingUpload[]): void {
  if (rows.length === 0) uploads.delete(key);
  else uploads.set(key, rows);
  emit();
}

/**
 * Put a row on screen, replacing whatever was there under the same id.
 *
 * Replacing rather than appending, because one file can be prepared twice: the
 * chat composer keeps it on disk and then sends it, and both go through the
 * same preparation. Appending would draw the same photograph as two tiles, one
 * of which nothing would ever finish.
 */
function putPending(key: Key, row: PendingUpload): void {
  const rows = pendingOf(key);
  const at = rows.findIndex((one) => one.id === row.id);

  setPending(key, at === -1 ? [...rows, row] : rows.map((one, i) => (i === at ? row : one)));
}

function patchPending(key: Key, id: string, patch: Partial<PendingUpload>): void {
  setPending(
    key,
    pendingOf(key).map((row) => (row.id === id ? { ...row, ...patch } : row)),
  );
}

function dropPending(key: Key, id: string): void {
  setPending(
    key,
    pendingOf(key).filter((row) => row.id !== id),
  );
}

/* ------------------------------------------------------------------ upload */

/**
 * Why an upload stopped, and whether trying again could ever help.
 *
 * The distinction is the whole of the queue's judgement. "The connection died"
 * is worth keeping the file for; "the server will never accept this" is a tile
 * that has to say so, because a queue that retries it for ever is a queue that
 * never empties.
 */
class UploadFailure extends Error {
  readonly retryable: boolean;

  constructor(message: string, retryable: boolean) {
    super(message);
    this.retryable = retryable;
  }
}

/**
 * POST the file, reporting how much of it has actually gone out.
 *
 * `fetch` cannot say — it has no upload progress event — and a 10 MB photo on a
 * phone's uplink takes long enough that a bare spinner reads as a hang. So this
 * one request is an XMLHttpRequest, the only thing in the browser that reports
 * it. The session cookie rides along because the request is same-origin (SPEC
 * §8), and the browser sets `Origin` on a POST, which the server's CSRF check
 * requires.
 */
function postFile(
  spaceId: string,
  file: File,
  fields: CreateAttachmentFields,
  onProgress: (fraction: number) => void,
): Promise<Attachment> {
  return new Promise((resolve, reject) => {
    const form = new FormData();
    // Metadata first, bytes second — the order a multipart body is convention-
    // ally written in, though the server buffers the whole thing either way.
    form.append("fields", JSON.stringify(fields));
    form.append("file", file, file.name);

    const request = new XMLHttpRequest();
    request.open("POST", `/api/spaces/${encodeURIComponent(spaceId)}/attachments`);
    request.responseType = "text";
    request.withCredentials = true;

    request.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable && event.total > 0) {
        onProgress(event.loaded / event.total);
      }
    });

    // A request that never arrived: no network, DNS, a connection dropped
    // mid-upload. Same outcome as being offline, and the same message — and the
    // one failure the queue exists for, because `navigator.onLine` says nothing
    // about whether the Wi-Fi it joined has an internet behind it.
    const died = () => reject(new UploadFailure(offlineMessage(), true));
    request.addEventListener("error", died);
    request.addEventListener("abort", died);
    request.addEventListener("timeout", died);

    request.addEventListener("load", () => {
      if (request.status < 200 || request.status >= 300) {
        // The server's own message is English, written for the API's callers; a
        // person gets the category in their own language instead.
        //
        // Only a 5xx is worth keeping the file for. A refused type, an expired
        // session and a target that has been deleted are all answers the next
        // attempt would repeat word for word.
        reject(
          request.status === 400 || request.status === 413
            ? new UploadFailure(unsupportedMessage(), false)
            : request.status === 401
              ? new UploadFailure(t("attachments.errorSessionExpired"), false)
              : request.status === 404
                ? new UploadFailure(t("attachments.errorTargetGone"), false)
                : new UploadFailure(t("attachments.errorUploadFailed"), request.status >= 500),
        );
        return;
      }

      let payload: unknown;
      try {
        payload = JSON.parse(request.responseText);
      } catch {
        reject(new UploadFailure(t("attachments.errorBadResponse"), false));
        return;
      }

      const parsed = attachmentResponseSchema.safeParse(payload);
      if (!parsed.success) {
        // A 2xx means the bytes landed; it is only the answer that made no
        // sense. Retrying would upload ten megabytes to be told the same thing.
        reject(new UploadFailure(t("attachments.errorBadResponse"), false));
        return;
      }

      resolve(parsed.data.attachment);
    });

    request.send(form);
  });
}

/* ------------------------------------------------------------ transmission */

/**
 * What became of one attempt to send a file.
 *
 * `wait` is the odd one and it earns its place: the space is locked on this
 * device right now, which is neither a failure to report nor a reason to spend
 * one of the file's attempts. Somebody will type the passphrase eventually and
 * the file is still perfectly sendable when they do.
 */
type Outcome =
  | { state: "sent" }
  | { state: "wait" }
  | { state: "retry"; reason: string }
  | { state: "failed"; reason: string };

/**
 * Seal one queued file and put it on the wire.
 *
 * Everything that depends on the space's key — the envelope and the encoded
 * name — happens here rather than when the file was queued, so a key rotated in
 * between is the key the file actually goes up under.
 */
async function transmit(entry: QueuedUpload, key: Key): Promise<Outcome> {
  /*
   * Not one byte until this space has actually been synced once.
   *
   * A space nothing has been pulled for has no row, and no row means the codec
   * falls back to `none` — so a file queued in an encrypted space and sent
   * during the first seconds of a cold start would go up **in the clear**. The
   * queue's whole reason for existing is files that outlive the moment they
   * were picked, which makes this its own failure to prevent.
   */
  const state = syncEngine.view(entry.spaceId);
  if (state.revision === 0 || !state.space) return { state: "wait" };

  const codec = syncEngine.codec(entry.spaceId);
  if (!codec) return { state: "wait" };

  let outgoing: File;
  try {
    outgoing = await sealForUpload(entry.spaceId, entry.file);
  } catch {
    // The codec was there a line ago, so this is the key having gone missing
    // between the two — the same "locked for now" as above.
    return { state: "wait" };
  }

  // Measured on what will actually be sent: the envelope adds a few dozen
  // bytes, and a file sitting exactly on the ceiling would otherwise go all the
  // way up to be refused there.
  if (outgoing.size > MAX_ATTACHMENT_BYTES) return { state: "failed", reason: tooLargeMessage() };

  const content = await codec.encode({ name: entry.name });

  try {
    await postFile(
      entry.spaceId,
      outgoing,
      {
        // The same id on a retry, so a request that succeeded but whose answer
        // was lost cannot become a second copy.
        id: entry.id,
        targetType: entry.targetType,
        targetId: entry.targetId,
        content,
      },
      (fraction) => patchPending(key, entry.id, { progress: fraction }),
    );
  } catch (cause) {
    const reason = cause instanceof Error ? cause.message : t("attachments.errorUploadFailed");
    // Anything that is not one of `postFile`'s own answers came from the code
    // around the request rather than the server, and is worth one more go.
    const retryable = cause instanceof UploadFailure ? cause.retryable : true;
    return retryable ? { state: "retry", reason } : { state: "failed", reason };
  }

  // The row exists on the server; this is what puts it in the mirror the strip
  // reads from. Awaited, so the tile stops being "pending" only once the real
  // thumbnail is there to replace it.
  await syncEngine.pull(entry.spaceId);
  return { state: "sent" };
}

/* ----------------------------------------------------------------- queuing */

/**
 * How many failed transmissions a file is given before it is admitted to.
 *
 * Not many, and not because the queue is precious: every attempt is the whole
 * file up a phone's uplink again. Six spread over the backoff below is around
 * five minutes of genuinely trying, which covers a lift, a tunnel and a
 * reconnect — and past that, a tile that says the file never went is a better
 * answer than a queue quietly grinding at it for the rest of the day.
 */
const MAX_UPLOAD_ATTEMPTS = 6;

/** Slower than the outbox's: what is being retried here costs megabytes. */
const RETRY_FLOOR_MS = 5_000;
const RETRY_CEILING_MS = 5 * 60_000;

const retryDelay = (attempts: number): number =>
  Math.min(RETRY_CEILING_MS, RETRY_FLOOR_MS * 2 ** Math.max(0, attempts - 1));

const targetOf = (entry: QueuedUpload): AttachmentTarget => ({
  type: entry.targetType,
  id: entry.targetId,
});

/**
 * Draw a queued file as a row, so it is somewhere on screen rather than gone.
 *
 * Idempotent: the drain calls it for every entry on every pass, and a file
 * queued in this session already has its row from the upload that queued it.
 */
function showQueued(entry: QueuedUpload): void {
  const key = keyOf(entry.spaceId, targetOf(entry));
  if (pendingOf(key).some((row) => row.id === entry.id)) return;

  setPending(key, [
    ...pendingOf(key),
    {
      id: entry.id,
      name: entry.name,
      bytes: entry.file.size,
      progress: 0,
      queued: true,
      error: null,
    },
  ]);
}

/**
 * Leave the reason on the row, and hand it back to the caller.
 *
 * Two audiences for one sentence: whatever is rendering the row gets it without
 * being asked, and a caller holding its own preview — the chat composer — gets
 * it as the answer it awaited.
 */
const failing =
  (key: Key, pendingId: string) =>
  (reason: string): string => {
    patchPending(key, pendingId, { error: reason });
    return reason;
  };

/** A file checked, scrubbed and drawn — or the sentence saying why it was not. */
type Prepared =
  | { entry: QueuedUpload; targetKey: Key; pendingId: string }
  | { reason: string };

/**
 * Everything that has to happen before a file goes anywhere.
 *
 * Shared by the two beginnings a file can have: sent this moment, or written to
 * disk to be sent when there is something to hang it off. Both need the same
 * refusals — a type the server will not take is worth knowing before ten
 * megabytes go up the wire — and both need the metadata off first, because a
 * file that waits a day for a network must not be waiting with somebody's
 * address in it.
 */
async function prepare(
  spaceId: string,
  uploadTarget: AttachmentTarget,
  file: File,
  options: UploadOptions,
): Promise<Prepared> {
  // The strip may be listing one target while an upload names another; the
  // pending tile belongs wherever the file is going.
  const targetKey = keyOf(spaceId, uploadTarget);
  const pendingId = options.id ?? uuidv7();
  const label =
    (options.name ?? file.name).trim().slice(0, 200) || t("attachments.fileFallback");

  putPending(targetKey, {
    id: pendingId,
    name: label,
    bytes: file.size,
    progress: 0,
    queued: false,
    error: null,
  });

  const fail = failing(targetKey, pendingId);

  /*
   * A locked space cannot take the file at all.
   *
   * This used to return without a word, before even a tile existed, on the
   * reasoning that a tile stuck at nought is a worse answer than none. It is —
   * but a tile that says why is better than both, and silence here was a file
   * that vanished on picking it.
   */
  const codec = syncEngine.codec(spaceId);
  if (!codec) return { reason: fail(t("attachments.errorSealFailed")) };

  // Refused here rather than after ten megabytes have gone up the wire.
  const mime = file.type.split(";")[0]?.trim().toLowerCase() ?? "";
  if (!(ALLOWED_MIME as readonly string[]).includes(mime)) {
    return { reason: fail(unsupportedMessage()) };
  }
  if (file.size > MAX_ATTACHMENT_BYTES) return { reason: fail(tooLargeMessage()) };
  if (file.size === 0) return { reason: fail(t("attachments.errorEmptyFile")) };

  /*
   * The metadata comes off before anything else touches the bytes, and before
   * the seal — a sealed photograph is octets nobody can look inside, including
   * us. `withoutMetadata` never throws: it hands the original back whenever it
   * cannot do better, and a file that could not be scrubbed still goes.
   */
  const scrubbed = await withoutMetadata(file);

  // Re-encoding can change the format, and then the name's extension is a lie —
  // `.avif` on a WebP. The row shows what is actually going up.
  const name = options.name
    ? label
    : (scrubbed.name.trim().slice(0, 200) || t("attachments.fileFallback"));
  if (scrubbed !== file) {
    patchPending(targetKey, pendingId, { name, bytes: scrubbed.size });
  }

  return {
    targetKey,
    pendingId,
    entry: {
      id: pendingId,
      spaceId,
      targetType: uploadTarget.type,
      targetId: uploadTarget.id,
      name,
      file: scrubbed,
      attempts: 0,
      queuedAt: Date.now(),
    },
  };
}

/** Put a file in the queue and leave its row saying nothing is wrong. */
async function hold(entry: QueuedUpload): Promise<boolean> {
  if (!(await queueUpload(entry))) return false;

  const key = keyOf(entry.spaceId, targetOf(entry));
  showQueued(entry);
  // Back to nought rather than stuck at the fraction the dead request reached:
  // none of those bytes counts towards the attempt that will actually land.
  // Off the queue and onto the wire: from here the number means bytes again.
  patchPending(key, entry.id, { progress: 0, queued: false, error: null });

  scheduleDrain(retryDelay(entry.attempts));
  return true;
}

/**
 * Files a live upload is already carrying, so the drain leaves them alone.
 *
 * The chat composer writes a photograph to disk the moment it is sent and then
 * transmits it itself. Both are wanted — the disk is what survives a reload,
 * the live request is what shows a percentage — but not at once: without this
 * the drain would put the same megabytes up the wire a second time. The server
 * would forgive it (the attachment id is the same, so it cannot become a second
 * copy) but a phone's uplink would not.
 */
const inFlight = new Set<string>();

let draining = false;
let drainTimer: ReturnType<typeof setTimeout> | null = null;
let lastPassAt = 0;

function scheduleDrain(delay: number): void {
  if (drainTimer) clearTimeout(drainTimer);
  drainTimer = setTimeout(() => {
    drainTimer = null;
    void drainQueue();
  }, delay);
}

/**
 * Send what is waiting, one file at a time.
 *
 * Two rules shape this, and both are about not breaking something else.
 *
 * **The outbox goes first.** A file hangs off a row, and a row created offline
 * exists only in the outbox until that drains. Sending the attachment first
 * would ask the server to hang a photograph off a wish it has never heard of —
 * a 404, and a permanent one. So a pass with anything still queued in the
 * outbox does nothing but come back later.
 *
 * **A failure does not stop the pass.** The outbox drains in order because an
 * item's create has to precede the update that ticks it off; nothing of the
 * sort holds between two attachments, each of which hangs off a row that
 * already exists. So one poisoned file is skipped rather than blocking every
 * photograph behind it.
 */
async function drainQueue(): Promise<void> {
  if (draining) return;

  /*
   * `online` fires on joining a Wi-Fi, not on reaching the internet through it,
   * and it can fire several times over as an interface settles. One pass every
   * few seconds is all any of them is worth; the rest is ten megabytes into a
   * captive portal.
   */
  const since = Date.now() - lastPassAt;
  if (since < RETRY_FLOOR_MS) {
    scheduleDrain(RETRY_FLOOR_MS - since);
    return;
  }

  // Nothing rescheduled here on purpose: there is no point waking up on a timer
  // to find the radio still off. `online` and the sync engine coming back are
  // what start the next pass.
  if (typeof navigator !== "undefined" && !navigator.onLine) return;

  if (syncEngine.getSnapshot().pending > 0) {
    scheduleDrain(RETRY_FLOOR_MS);
    return;
  }

  // Read last of the four, because this is the one that costs something.
  const queued = await queuedUploads();
  if (queued.length === 0) return;

  draining = true;
  lastPassAt = Date.now();
  let soonest = 0;

  const later = (delay: number): void => {
    soonest = soonest === 0 ? delay : Math.min(soonest, delay);
  };

  try {
    for (const entry of queued) {
      if (inFlight.has(entry.id)) continue;

      const key = keyOf(entry.spaceId, targetOf(entry));
      showQueued(entry);

      const outcome = await transmit(entry, key);

      if (outcome.state === "sent") {
        await dropQueuedUpload(entry.id);
        dropPending(key, entry.id);
        continue;
      }

      if (outcome.state === "wait") {
        // Locked on this device. Nothing to count and nothing to say: a
        // passphrase is not something a timer can hurry.
        later(RETRY_CEILING_MS);
        continue;
      }

      if (outcome.state === "failed") {
        await dropQueuedUpload(entry.id);
        patchPending(key, entry.id, { error: outcome.reason });
        continue;
      }

      const attempts = entry.attempts + 1;

      if (attempts >= MAX_UPLOAD_ATTEMPTS) {
        /*
         * Given up on — out of the queue, but not off the screen. The row stays
         * and wears the reason, exactly as the outbox's abandoned operations
         * do: a file that quietly stopped existing is the failure this whole
         * queue was built to end.
         */
        await dropQueuedUpload(entry.id);
        patchPending(key, entry.id, { error: t("attachments.errorGaveUp") });
        continue;
      }

      await queueUpload({ ...entry, attempts });
      patchPending(key, entry.id, { progress: 0 });
      later(retryDelay(attempts));
    }
  } finally {
    draining = false;
  }

  if (soonest > 0) scheduleDrain(soonest);
}

/**
 * Start watching for a network, once per tab.
 *
 * Called from the hook rather than at module load: an import should not attach
 * a window listener, and every screen that could show a queued file mounts this
 * hook anyway. The first call is also what puts the rows for files queued in an
 * earlier session back on screen.
 */
let watching = false;

function watchForNetwork(): void {
  if (watching || typeof window === "undefined") return;
  watching = true;

  window.addEventListener("online", () => void drainQueue());

  void queuedUploads().then((queued) => {
    for (const entry of queued) showQueued(entry);
    void drainQueue();
  });
}

/* --------------------------------------------------------------- decoding */

/**
 * Decode an attachment's content for rendering.
 *
 * Same shape and same reason as the wishes screen's decoder: `none` mode carries
 * plain JSON, so rendering stays synchronous. An e2ee blob cannot be read this
 * way, and when that mode lands the decode moves to where a delta is applied.
 */
function readContent(blob: ContentBlob | undefined): AttachmentContent | null {
  const value = plaintextOf(blob);
  if (value === undefined) return null;
  const parsed = attachmentContentSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

function toView(row: Attachment): AttachmentView {
  const content = readContent(row.content);

  return {
    id: row.id,
    url: row.url,
    mime: row.mime,
    bytes: row.bytes,
    // A name that cannot be read — an e2ee space, or a blob written by a newer
    // client — still gets a tile rather than an empty square.
    name: content?.name ?? t("attachments.fileFallback"),
    ...(content?.caption ? { caption: content.caption } : {}),
    isImage: row.mime.startsWith("image/"),
    addedBy: row.addedBy,
    createdAt: row.createdAt,
  };
}

/* -------------------------------------------------------------------- hook */

export function useAttachments(
  spaceId: string | null,
  target: AttachmentTarget | null = null,
): AttachmentActions {
  const synced = useSyncExternalStore(syncEngine.subscribe, syncEngine.getSnapshot);
  const local = useSyncExternalStore(subscribe, snapshot);
  // Only a dependency of the memo below: `toView` names an unreadable file in
  // the current language, so the views have to be built again when it changes.
  const locale = useLocale();

  const key = spaceId ? keyOf(spaceId, target) : null;

  // Tab-wide, and started by whichever strip mounts first — including the one
  // that mounts on a cold start with files still waiting from last time.
  useEffect(watchForNetwork, []);

  /*
   * The engine's word, not `navigator.onLine`.
   *
   * It says online only once a request or the WebSocket actually got through,
   * which is the difference between a network and a Wi-Fi symbol. And `pending`
   * at nought means the outbox has drained — so the rows these files hang off
   * are on the server, and an attachment sent now has something to hang from.
   */
  useEffect(() => {
    if (synced.status === "online" && synced.pending === 0) void drainQueue();
  }, [synced.status, synced.pending]);

  const { attachments, loading } = useMemo(() => {
    if (!spaceId) return { attachments: [], loading: false };

    const state = syncEngine.view(spaceId);
    const rows = target
      ? selectAttachments(state, target.type, target.id)
      : Object.values(state.attachments);

    return {
      attachments: rows.filter((row) => !removed.has(row.id)).map(toView),
      // Revision zero means this device has not had an answer for this space
      // yet — not that the space is empty.
      loading: state.revision === 0,
    };
    // `key` stands in for `target`, whose identity changes on every render.
  }, [spaceId, key, synced, local, locale]);

  const upload = useCallback(
    async (
      uploadTarget: AttachmentTarget,
      file: File,
      options: UploadOptions = {},
    ): Promise<string | null> => {
      if (!spaceId) return null;

      const ready = await prepare(spaceId, uploadTarget, file, options);
      if ("reason" in ready) return ready.reason;

      const { entry, targetKey, pendingId } = ready;
      const fail = failing(targetKey, pendingId);

      // No radio at all: straight to the queue, without a request that has
      // nowhere to go. A queue that could not keep the file says so instead of
      // promising to send something it has dropped.
      if (typeof navigator !== "undefined" && !navigator.onLine) {
        watchForNetwork();
        return (await hold(entry)) ? null : fail(offlineMessage());
      }

      // Held by this request now, whether or not a copy is already on disk —
      // the drain must not carry the same megabytes alongside it.
      inFlight.add(pendingId);
      let outcome;
      try {
        outcome = await transmit(entry, targetKey);
      } finally {
        inFlight.delete(pendingId);
      }

      if (outcome.state === "sent") {
        // Unconditional: this may or may not have been written to disk first
        // (see `keep`), and asking costs more than the delete does.
        await dropQueuedUpload(pendingId).catch(() => undefined);
        dropPending(targetKey, pendingId);
        return null;
      }

      // The connection died mid-flight, or the space is locked this second.
      // Either way the file is still sendable, and losing it here is the thing
      // the queue exists to stop.
      if (outcome.state !== "failed") {
        watchForNetwork();
        const attempts = outcome.state === "retry" ? 1 : 0;
        if (await hold({ ...entry, attempts })) return null;
      }

      return fail(outcome.state === "wait" ? t("attachments.errorSealFailed") : outcome.reason);
    },
    [spaceId],
  );

  const keep = useCallback(
    async (
      keepTarget: AttachmentTarget,
      file: File,
      options: UploadOptions = {},
    ): Promise<string | null> => {
      if (!spaceId) return null;

      const ready = await prepare(spaceId, keepTarget, file, options);
      if ("reason" in ready) return ready.reason;

      // So a reload, or simply the network coming back later, finds it.
      watchForNetwork();

      if (!(await queueUpload(ready.entry))) {
        return failing(ready.targetKey, ready.pendingId)(offlineMessage());
      }

      patchPending(ready.targetKey, ready.pendingId, { queued: true });
      return null;
    },
    [spaceId],
  );

  const sendKept = useCallback(
    async (pendingId: string): Promise<string | null> => {
      if (!spaceId) return null;

      /*
       * Read back rather than kept in memory beside the disk copy.
       *
       * The stored file is the one the drain would send, metadata already off,
       * so sending it is the same act whichever of the two gets there first —
       * and there is no second scrub of a ten-megabyte photograph.
       */
      const entry = await queuedUpload(pendingId);
      // Gone means the drain sent it while the message was landing. Nothing
      // failed, so nothing is said.
      if (!entry) return null;

      const targetKey = keyOf(entry.spaceId, targetOf(entry));

      inFlight.add(pendingId);
      let outcome;
      try {
        outcome = await transmit(entry, targetKey);
      } finally {
        inFlight.delete(pendingId);
      }

      if (outcome.state === "sent") {
        await dropQueuedUpload(pendingId).catch(() => undefined);
        dropPending(targetKey, pendingId);
        return null;
      }

      // Still on disk, so the drain will try again — the tile goes back to
      // waiting rather than wearing a failure it may not deserve.
      if (outcome.state !== "failed") {
        patchPending(targetKey, pendingId, { progress: 0, queued: true });
        scheduleDrain(retryDelay(1));
        return null;
      }

      await dropQueuedUpload(pendingId).catch(() => undefined);
      return failing(targetKey, pendingId)(outcome.reason);
    },
    [spaceId],
  );

  /** Take a file off the disk queue and off the screen. */
  const forget = useCallback(async (pendingId: string): Promise<void> => {
    if (!spaceId) return;
    await dropQueuedUpload(pendingId).catch(() => undefined);
    const key = keyHolding(spaceId, pendingId);
    if (key) dropPending(key, pendingId);
  }, [spaceId]);

  const remove = useCallback(
    async (attachmentId: string) => {
      if (!spaceId || !key) return;

      removeErrors.delete(key);
      removed.add(attachmentId);
      emit();

      try {
        const response = await fetch(
          `/api/spaces/${encodeURIComponent(spaceId)}/attachments/${encodeURIComponent(attachmentId)}`,
          { method: "DELETE", credentials: "same-origin" },
        );

        // 404 means somebody else got there first — the outcome that was asked
        // for, so it is not an error to report.
        if (!response.ok && response.status !== 404) {
          removed.delete(attachmentId);
          removeErrors.set(key, t("attachments.errorRemoveFailed"));
          emit();
          return;
        }
      } catch {
        removed.delete(attachmentId);
        removeErrors.set(key, offlineMessage());
        emit();
        return;
      }

      await syncEngine.pull(spaceId);
      // The mirror has the tombstone now, so the local override can go.
      removed.delete(attachmentId);
      emit();
    },
    [spaceId, key],
  );

  /*
   * Without a target this is every upload in the space, and it is a fresh array
   * — hence the memo, so a screen holding it in a dependency list does not see a
   * new one on every render. `key` stands in for `target` here as it does above.
   */
  const pending = useMemo(() => {
    if (!spaceId || !key) return [];
    return target ? pendingOf(key) : pendingIn(spaceId);
  }, [spaceId, key, local]);

  const dismiss = useCallback(
    (pendingId: string) => {
      if (!spaceId || !key) return;
      // No target means the row could be under any of the space's keys: the
      // composer dismisses an upload whose message id it never held on to.
      const holder = target ? key : keyHolding(spaceId, pendingId);
      if (holder) dropPending(holder, pendingId);

      // A waiting file goes with its tile. Without this the next drain would
      // put the row straight back, and dismissing would be a button that undoes
      // itself a few seconds later.
      void dropQueuedUpload(pendingId);
    },
    [spaceId, key],
  );

  return {
    attachments,
    pending,
    loading,
    error: key ? (removeErrors.get(key) ?? null) : null,
    upload,
    keep,
    sendKept,
    forget,
    remove,
    dismiss,
  };
}
